/**
 * WP-06C-B — Consumer-facing copy for hosted Loop refusals.
 *
 * The backend answers with a small categorical vocabulary. This module is the ONLY
 * place that turns a reason code into a sentence, and it does so with a total
 * `switch` per vocabulary, so a new reason cannot silently fall through to a raw
 * string. Nothing here interpolates a provider message, SQL text, constraint
 * name, identifier, token, or exception: the input is a closed union of literals.
 *
 * Copy is plain-language and honest about who acts next, per the L∞seEnd™ voice.
 */

import type { LoopCreationFailureReason } from "../creation/creationErrors";
import type { LoopLifecycleFailureReason } from "../lifecycle/errors";
import type { LoopReadFailureReason } from "../read/loopRepository";

export function lifecycleFailureMessage(reason: LoopLifecycleFailureReason): string {
  switch (reason) {
    case "validation_failed":
      return "That change could not be applied as written. Adjust it and try again.";
    case "not_found_or_not_authorized":
      return "This Loop is no longer available to you.";
    case "not_authorized":
      return "You do not have permission to make that change.";
    case "version_conflict":
      return "This Loop changed somewhere else. It has been refreshed — review it and try again.";
    case "invalid_transition":
      return "This can’t move to that state from where it is now.";
    case "resolution_requirements_not_met":
      return "This isn’t ready to be marked resolved yet. Something required still needs confirmation.";
    case "idempotency_conflict":
      return "That request conflicted with another change. Try again.";
    case "workspace_not_active":
      return "This workspace is archived, so Loops in it can no longer change.";
    case "account_not_active":
      return "Your account is not active, so no changes can be made.";
    case "transient_conflict":
      return "Something else was updating this Loop. Try again in a moment.";
    case "unavailable":
      return "Loops are temporarily unavailable. Nothing was changed.";
  }
}

/**
 * WP-A-UI — Copy for a refused evidence-backed verification. The backend answers
 * with the same lifecycle vocabulary; here the sentence is phrased for the
 * verification gesture rather than for resolution. Anything not specific to
 * verification falls through to the general lifecycle copy.
 */
export function evidenceVerificationFailureMessage(reason: LoopLifecycleFailureReason): string {
  switch (reason) {
    case "resolution_requirements_not_met":
      return "This evidence can no longer verify this outcome. Its current status has been refreshed.";
    case "not_authorized":
      return "You don't have permission to verify this condition.";
    case "validation_failed":
      return "This evidence cannot be used for verification.";
    default:
      return lifecycleFailureMessage(reason);
  }
}

export function creationFailureMessage(reason: LoopCreationFailureReason): string {
  switch (reason) {
    case "not_found_or_not_authorized":
      return "This Loop could not be saved to your workspace.";
    case "role_not_permitted":
      return "You do not have permission to create Loops here.";
    case "account_not_active":
      return "Your account is not active, so new Loops cannot be created.";
    case "workspace_not_active":
      return "This workspace is archived, so new Loops cannot be created.";
    case "validation_failed":
      return "Some details were missing or unusable, so the Loop was not saved.";
    case "idempotency_conflict":
      return "That save conflicted with another one. Try again.";
    case "transient_conflict":
      return "Something else was writing at the same time. Try saving again.";
    case "unavailable":
      return "Loops are temporarily unavailable. Nothing was saved.";
  }
}

export function readFailureMessage(reason: LoopReadFailureReason): string {
  switch (reason) {
    case "not_found":
      return "This Loop is not available to you.";
    case "unavailable":
      return "Your Loops are temporarily unavailable.";
  }
}
